<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Tooltip,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
} from 'chart.js';
import KanbanReportWidget from 'kanban/components/reports/KanbanReportWidget.vue';
import { CHART_FONT_FAMILY } from 'dashboard/routes/dashboard/settings/reports/constants';

// Conversas por dia no periodo. Serie unica: sem legenda (o titulo nomeia),
// linha fina, grade recessiva, tooltip por ponto.
const props = defineProps({
  // [{ value, timestamp }] (timeseries do backend, bucket por dia)
  points: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

ChartJS.register(
  Tooltip,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler
);

const { t, locale } = useI18n();

const SERIES_COLOR = '#2a78d6';

const labels = computed(() =>
  props.points.map(point =>
    new Intl.DateTimeFormat(locale.value?.replace('_', '-'), {
      day: '2-digit',
      month: 'short',
      timeZone: 'UTC',
    }).format(new Date(point.timestamp * 1000))
  )
);

const isEmpty = computed(
  () => !props.points.length || props.points.every(point => !point.value)
);

const collection = computed(() => ({
  labels: labels.value,
  datasets: [
    {
      label: t('VINCTO_HOME.TREND.SERIES'),
      data: props.points.map(point => point.value),
      borderColor: SERIES_COLOR,
      pointBackgroundColor: SERIES_COLOR,
      backgroundColor: 'rgba(42, 120, 214, 0.08)',
      borderWidth: 2,
      pointRadius: props.points.length > 45 ? 0 : 2.5,
      pointHitRadius: 12,
      tension: 0.3,
      fill: true,
    },
  ],
}));

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 0 },
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: { displayColors: false },
  },
  scales: {
    x: {
      ticks: {
        fontFamily: CHART_FONT_FAMILY,
        maxTicksLimit: 8,
        maxRotation: 0,
      },
      grid: { drawOnChartArea: false },
    },
    y: {
      beginAtZero: true,
      ticks: { fontFamily: CHART_FONT_FAMILY, precision: 0 },
      grid: { color: 'rgba(128, 128, 128, 0.12)' },
    },
  },
};
</script>

<template>
  <KanbanReportWidget
    class="!rounded-2xl !border-n-weak !bg-white dark:!bg-n-solid-2"
    :title="t('VINCTO_HOME.TREND.TITLE')"
    :info-text="t('VINCTO_HOME.TREND.INFO')"
    :is-loading="isLoading"
    :is-empty="isEmpty"
    :empty-text="t('VINCTO_HOME.TREND.EMPTY')"
  >
    <div class="h-64">
      <Line :data="collection" :options="chartOptions" />
    </div>
  </KanbanReportWidget>
</template>
